import React from "react";
import {
  Box,
  Stack,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import TuneIcon from "@mui/icons-material/Tune";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import PaymentsIcon from "@mui/icons-material/Payments";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { actionCentreRegistry } from "./ActionCentre/registry";
import {
  isRequestTypeAccessible,
  workAdjustmentTabOf,
} from "./ActionCentre/requestTypeAccess";
import { checkViewerPermission } from "services/utils/HelperFunction";
import { designWeights } from "services/constants/designTokens";
import { dashboard } from "./dashboardStyles";

// Per-type breakdown of the employee's own pending requests. Rows are driven by
// the same actionCentreRegistry (one row per request type) that feeds the
// Pending Requests KPI, and the pending counts come from the shared
// useMyPendingRequests fetch passed down from Home, so the two never diverge.
const TYPE_META = {
  leave: { label: "Leave", icon: BeachAccessIcon, color: dashboard.colors.green },
  overtime: { label: "Overtime", icon: AccessTimeIcon, color: dashboard.colors.amber },
  offDayDuty: { label: "Off-Day Duty", icon: EventAvailableIcon, color: dashboard.colors.blue },
  workAdjustment: { label: "Work Adjustment", icon: TuneIcon, color: dashboard.colors.purple },
  attendanceCorrection: { label: "Attendance Correction", icon: EditCalendarIcon, color: dashboard.colors.blue },
  advanceSalary: { label: "Salary Advance", icon: PaymentsIcon, color: "#EE46BC" },
};

const WORK_ADJUSTMENT_ROWS = [
  { tab: "permission", label: "Permission", submodule: "Permission", icon: TuneIcon, color: dashboard.colors.purple },
  { tab: "onduty", label: "On Duty", submodule: "On Duty", icon: TuneIcon, color: dashboard.colors.purple },
  { tab: "compoff", label: "Comp Off", submodule: "Comp Off", icon: TuneIcon, color: dashboard.colors.purple },
  { tab: "break", label: "Break", submodule: "Break", icon: TuneIcon, color: dashboard.colors.purple },
];

export const MyRequestsSummarySection = ({ countsByType, pending, loading }) => {
  const history = useHistory();
  const { userScreenAccess } = useSelector((state) => state.tokenReducer);

  // Work Adjustment is no longer a single destination: the My Requests menu
  // lists Permission / On Duty / Comp Off / Break as separate screens, so this
  // card splits that one registry entry into the same four rows. Each row is
  // counted from this employee's own pending items (matched on the backend's
  // attendance_type string), links straight to its own tab, and is gated on
  // its own "My Request" submodule -- the same check that screen applies.
  const workAdjustmentRows = WORK_ADJUSTMENT_ROWS.filter((row) =>
    checkViewerPermission(userScreenAccess?.module_details, "My Request", row.submodule),
  ).map((row) => ({
    key: `workAdjustment-${row.tab}`,
    label: row.label,
    icon: row.icon,
    color: row.color,
    path: `/admin/myzone/apply?view=work-adjustment&tab=${row.tab}`,
    count: (pending || []).filter(
      (item) =>
        item.type === "workAdjustment" &&
        workAdjustmentTabOf(item) === row.tab,
    ).length,
  }));

  const rows = actionCentreRegistry.flatMap((entry) => {
    if (entry.type === "workAdjustment") return workAdjustmentRows;
    if (!isRequestTypeAccessible(userScreenAccess?.module_details, entry.type)) return [];
    const meta = TYPE_META[entry.type] || {
      label: entry.itemLabel || entry.type,
      icon: TuneIcon,
      color: dashboard.colors.subText,
    };
    return [
      {
        key: entry.type,
        label: meta.label,
        icon: meta.icon,
        color: meta.color,
        path: entry.viewAllPath,
        count: countsByType?.[entry.type] || 0,
      },
    ];
  });

  // Same self-hiding pattern as this row's other two cards, UpcomingScheduleSection
  // and UpcomingHolidaysSection (see Home/index.js's row-2 comment) -- a user
  // with no viewer access to any "My Request" type (e.g. the whole module cut
  // off) used to still get this card, just with an empty body under the "My
  // Requests Summary" header and a "View All" link into a page with nothing
  // on it. Hiding it lets the auto-fit grid row give its space to whichever
  // of its siblings do render, instead of reserving a column for a
  // near-empty card.
  if (rows.length === 0) return null;

  return (
    <Box sx={dashboard.cardSx}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Typography fontWeight={designWeights.heading} sx={{ color: dashboard.colors.text }}>
          My Requests Summary
        </Typography>
        <Typography
          sx={dashboard.linkSx}
          onClick={() => history.push("/admin/myzone/apply")}
        >
          View All →
        </Typography>
      </Stack>
      {loading ? (
        <CircularProgress size={22} />
      ) : (
        <Box>
          {rows.map((row, idx) => {
            const Icon = row.icon;
            const meta = row;
            const count = row.count;
            return (
              <Box key={row.key}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ py: 1.25, cursor: "pointer" }}
                  onClick={() => history.push(row.path)}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={dashboard.iconWrap(meta.color)}>
                      <Icon fontSize="small" />
                    </Box>
                    <Typography fontWeight={designWeights.medium} sx={{ color: dashboard.colors.text }}>
                      {meta.label}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="body2"
                      sx={{
                        color: count > 0 ? dashboard.colors.amber : dashboard.colors.subText,
                        fontWeight: designWeights.semibold,
                      }}
                    >
                      {count} Pending
                    </Typography>
                    <ChevronRightIcon sx={{ color: dashboard.colors.subText }} />
                  </Stack>
                </Stack>
                {idx !== rows.length - 1 && <Divider />}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
